/* ============================================================
   Rose Lamp PDP - theming + interactivity
   Loaded (defer) by every rose-* section; everything is guarded
   because the same <script> tag appears once per section.
   Slug contract: roseHandleize() must match Liquid `| handleize`
   for the option values (Red, Pink, Purple, White, Multi-Color).
   ============================================================ */
if (!window.RosePDP) {
  window.RosePDP = (function () {
    'use strict';

    function roseHandleize(value) {
      return String(value)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    // Shopify's CDN resizes /files/ URLs through a `width` query param, so the
    // merchant can paste a full-size URL and still ship a right-sized image.
    // Non-Shopify URLs pass through untouched.
    function roseCdnResize(url, width) {
      if (!url || !/\/\/([a-z0-9-]+\.)*cdn\.shopify\.com\//i.test(url)) return url;
      if (/[?&]width=/.test(url)) return url;
      // Resizing an animated GIF returns one static frame, and SVGs gain nothing.
      if (/\.(gif|svg)(\?|$)/i.test(url)) return url;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    }

    function roseSetTheme(slug, label) {
      if (!slug) return;
      document.documentElement.setAttribute('data-rose-theme', slug);
      // Multi-Color's layered lamp is fetched on first use only (see the
      // comment in rose-main-product.liquid for why lazy loading is not enough).
      if (slug === 'multi-color') {
        document.querySelectorAll('img[data-rose-multi-src]').forEach(function (img) {
          img.src = img.getAttribute('data-rose-multi-src');
          img.removeAttribute('data-rose-multi-src');
        });
      }
      if (label) {
        document.querySelectorAll('[data-rose-variant-name]').forEach(function (el) {
          // A color may override its own caption, e.g. Multi-Color shows
          // "20+ Colors/Options!" rather than the bare option value.
          el.textContent = el.getAttribute('data-rose-label-' + slug) || label;
        });
      }
      // Highlight the matching "choose the glow" card and solid swatches.
      document.querySelectorAll('.rose-mood-card[data-rose-color-value], .rose-solid-swatch[data-rose-color-value]').forEach(function (el) {
        el.classList.toggle('is-active', roseHandleize(el.dataset.roseColorValue) === slug);
      });
      // Swap any per-variant images (lifestyle, grid cards, ...) that declare
      // data-rose-swap + data-src-<slug> attributes.
      document.querySelectorAll('[data-rose-swap]').forEach(function (img) {
        var src = img.getAttribute('data-src-' + slug);
        if (src) img.setAttribute('src', src);
      });
      // Point any custom gallery at this variant's image set.
      document.querySelectorAll('rose-gallery').forEach(function (g) {
        if (g.setVariant) g.setVariant(slug);
      });
      // Bundle tiles: when the selected variant is excluded from the per-unit
      // color selects (e.g. Multi-Color), hide the selects - the form then
      // falls back to "tile quantity x selected variant", and we reset any
      // stale per-unit ids from a previous color.
      document.querySelectorAll('.quantity-breaks-container[data-rose-excluded]').forEach(function (qc) {
        var excluded = (qc.dataset.roseExcluded || '').split(',').filter(Boolean);
        var isExcluded = excluded.indexOf(slug) !== -1;
        qc.classList.toggle('rose-units-hidden', isExcluded);
        if (isExcluded && typeof window.resetIdInputValue === 'function') {
          try { window.resetIdInputValue(); } catch (e) { /* form not present */ }
        }
      });
      // Keep a multi-unit bundle's per-lamp colors on the color just chosen.
      roseAutofillBundleUnits();
    }

    // --- Bundle per-unit color autofill --------------------------------------
    // Picking a multi-lamp tile should pre-fill every lamp with the color the
    // shopper is already looking at, leaving them free to change individual ones.
    //
    // The theme's own resetIdInputValue() tries this but only sets the `selected`
    // ATTRIBUTE on the option, which does not move a live <select>. Setting the
    // property is what actually changes it.
    function roseCurrentVariantId() {
      var input = document.querySelector('.rose-main-section product-form input[name="id"]');
      if (!input) input = document.querySelector('.rose-main-section input[name="id"]');
      if (!input) return '';
      // `value` may already hold a comma-joined bundle list, so prefer the
      // single id Liquid stamped on the element.
      return input.dataset.selectedId || String(input.value || '').split(',')[0] || '';
    }

    function roseAutofillBundleUnits() {
      var id = roseCurrentVariantId();
      if (!id) return;
      document.querySelectorAll('.rose-main-section .quantity-break__variants').forEach(function (group) {
        // Only the tile the shopper actually selected should be rewritten.
        var tile = group.closest('.quantity-break-item') || group.parentElement;
        var radio = tile && tile.querySelector('input[type="radio"][name="quantity"]');
        if (!radio) {
          var prev = group.previousElementSibling;
          while (prev && !radio) {
            radio = prev.querySelector && prev.querySelector('input[type="radio"][name="quantity"]');
            prev = prev.previousElementSibling;
          }
        }
        if (radio && !radio.checked) return;

        group.querySelectorAll('.quantity-break__variant-select').forEach(function (sel) {
          // Colors excluded from the bundle (e.g. Multi-Color) have no option.
          if (!sel.querySelector('option[value="' + id + '"]')) return;
          sel.value = id;
          // Fire even when the value already matched. resetIdInputValue() moves a
          // pristine <select> by setting the `selected` attribute, so the display
          // can be right while the items[n][id] hidden inputs were never built -
          // the form would then post one lamp instead of three. This event is what
          // rebuilds them.
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }

    // Selecting a bundle tile fires the theme's resetIdInputValue() first; run
    // the autofill after it so ours is the value that sticks.
    document.addEventListener('change', function (event) {
      var t = event.target;
      if (!t.matches || !t.matches('input[type="radio"][name="quantity"]')) return;
      setTimeout(function () {
        var tile = t.nextElementSibling;
        if (!tile || !tile.classList || !tile.classList.contains('quantity-break')) {
          tile = t.id ? document.querySelector('label[for="' + t.id + '"]') : null;
        }
        var hasUnits = tile && tile.querySelector('.quantity-break__variant-select');
        if (!hasUnits) {
          // Single-lamp tile. The theme only wires resetIdInputValue() to the
          // MULTI-unit radios, so coming back down from a bundle left the
          // comma-joined id and the items[] inputs in place - the form would
          // post three lamps for a one-lamp selection. Clear them.
          if (typeof window.resetIdInputValue === 'function') {
            try { window.resetIdInputValue(); } catch (e) { /* form not present */ }
          }
          return;
        }
        roseAutofillBundleUnits();
      }, 0);
    });

    // --- Proxy color selectors (gallery color row, "choose the glow" cards):
    // select the real variant radio and let the full pipeline run.
    document.addEventListener('click', function (event) {
      var el = event.target.closest('[data-rose-color-value]');
      if (!el) return;
      var value = el.dataset.roseColorValue;
      var input = document.querySelector(
        'variant-radios input[type="radio"][value="' + CSS.escape(value) + '"]'
      );
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (!input) {
        // Fallback (renamed value / dropdown picker): still retheme the page.
        roseSetTheme(roseHandleize(value), value);
      }
    });

    // --- Instant path: retheme the moment a color radio is clicked ---
    document.addEventListener('change', function (event) {
      var input = event.target;
      if (!input.matches || !input.matches('input[type="radio"]')) return;
      if (!input.closest('variant-radios')) return;
      roseSetTheme(roseHandleize(input.value), input.value);
    });

    // --- Authoritative path: after the Section Rendering fetch completes ---
    function bindVariantChange() {
      if (typeof subscribe !== 'function' || !window.PUB_SUB_EVENTS) return false;
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event && event.data && event.data.variant;
        if (variant && variant.option1) {
          roseSetTheme(roseHandleize(variant.option1), variant.option1);
        }
      });
      return true;
    }
    // pubsub.js is a deferred head script, so it runs before this file,
    // but guard with a retry in case of unusual load order.
    if (!bindVariantChange()) {
      document.addEventListener('DOMContentLoaded', bindVariantChange);
    }

    // --- Append a right arrow to the add-to-cart buttons ---
    // (buy-buttons.liquid is shared with other templates, so the arrow is
    // added at runtime here rather than by forking that snippet.)
    function injectAtcArrow() {
      document
        .querySelectorAll('.rose-main-section button[name="add"], .sticky-atc button[name="add"]')
        .forEach(function (btn) {
          if (btn.querySelector('.rose-atc-arrow')) return;
          var label = btn.querySelector('span') || btn;
          var arrow = document.createElement('span');
          arrow.className = 'rose-atc-arrow';
          arrow.setAttribute('aria-hidden', 'true');
          arrow.textContent = '→';
          label.appendChild(arrow);
        });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', injectAtcArrow);
    } else {
      injectAtcArrow();
    }
    document.addEventListener('shopify:section:load', injectAtcArrow);

    // --- Shrink every ® to a superscript mark ---
    // It arrives from many places (settings text, product.title, rich text), so
    // it is wrapped at runtime rather than in each template.
    function shrinkRegisteredMarks(root) {
      var scope = root || document.querySelector('.rose-main-section');
      if (!scope) return;
      var roots = root ? [root] : Array.from(
        document.querySelectorAll(
          '.rose-main-section, .rose-feature-list, .rose-image-grid, .rose-faq,' +
          '.rose-final-cta, .rose-mood-cards, .rose-dark-callout, .sticky-atc'
        )
      );
      roots.forEach(function (el) {
        var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
          acceptNode: function (node) {
            if (!node.nodeValue || node.nodeValue.indexOf('®') === -1) return NodeFilter.FILTER_REJECT;
            var p = node.parentNode;
            if (!p || p.nodeName === 'SCRIPT' || p.nodeName === 'STYLE') return NodeFilter.FILTER_REJECT;
            if (p.classList && p.classList.contains('rose-reg')) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
          }
        });
        var targets = [];
        while (walker.nextNode()) targets.push(walker.currentNode);
        targets.forEach(function (node) {
          var parts = node.nodeValue.split('®');
          var frag = document.createDocumentFragment();
          parts.forEach(function (part, i) {
            if (part) frag.appendChild(document.createTextNode(part));
            if (i < parts.length - 1) {
              var s = document.createElement('span');
              s.className = 'rose-reg';
              s.textContent = '®';
              frag.appendChild(s);
            }
          });
          node.parentNode.replaceChild(frag, node);
        });
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { shrinkRegisteredMarks(); });
    } else {
      shrinkRegisteredMarks();
    }
    document.addEventListener('shopify:section:load', function (e) { shrinkRegisteredMarks(e.target); });

    // --- Anchor repair -------------------------------------------------------
    // In-page links point at stable ids (#rose-reviews) that are printed by the
    // rose sections themselves. Hiding a section in the theme editor removes it
    // from the page entirely, taking its id with it and leaving every link to it
    // pointing at nothing - a click that silently does nothing.
    //
    // So for each of those links, fall back to the section wrapper that covers
    // the same ground. Shopify prefixes a JSON template's section ids with
    // `template--<id>__`, which is why these match on the SUFFIX rather than on
    // `#shopify-section-<key>`, which never resolves in a JSON template.
    //
    // Repointing the href (rather than intercepting the click) keeps native
    // anchor behaviour: the browser scrolls, and the URL hash still updates.
    var ROSE_ANCHOR_FALLBACKS = {
      // The reviews header is optional - merchants running a reviews app often
      // hide it, since the app renders its own summary.
      'rose-reviews': ['rose_reviews_app', 'rose_reviews_header'],
      'rose-faq-jump': ['rose_faq'],
      'rose-why': ['rose_feature_list']
    };
    function roseRepairAnchors() {
      Object.keys(ROSE_ANCHOR_FALLBACKS).forEach(function (id) {
        var links = document.querySelectorAll('a[href="#' + id + '"]');
        if (!links.length) return;
        if (document.getElementById(id)) return; // anchor is present, nothing to do
        var keys = ROSE_ANCHOR_FALLBACKS[id];
        var target = null;
        for (var i = 0; i < keys.length && !target; i++) {
          target = document.querySelector('[id$="__' + keys[i] + '"], [id="shopify-section-' + keys[i] + '"]');
        }
        links.forEach(function (a) {
          if (target) {
            a.setAttribute('href', '#' + target.id);
          } else {
            // Nothing on the page covers this any more. Strip the href so it
            // stops rendering as a link that goes nowhere.
            a.removeAttribute('href');
            a.classList.add('rose-link-inert');
          }
        });
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', roseRepairAnchors);
    } else {
      roseRepairAnchors();
    }
    // Sections mount and unmount as the merchant works in the theme editor.
    document.addEventListener('shopify:section:load', roseRepairAnchors);
    document.addEventListener('shopify:section:unload', roseRepairAnchors);

    // --- Theme editor: re-apply theme when the init section reloads ---
    document.addEventListener('shopify:section:load', function (event) {
      var el = event.target.querySelector('[data-rose-default]');
      if (el) roseSetTheme(el.dataset.roseDefault);
    });

    return { handleize: roseHandleize, setTheme: roseSetTheme, cdnResize: roseCdnResize };
  })();
}

/* --- "Choose the glow" cards: clicks handled by the global
       [data-rose-color-value] delegate; just sync the active card on init --- */
if (!customElements.get('rose-color-moods')) {
  customElements.define(
    'rose-color-moods',
    class RoseColorMoods extends HTMLElement {
      connectedCallback() {
        var current = document.documentElement.getAttribute('data-rose-theme');
        if (current) window.RosePDP.setTheme(current);
      }
    }
  );
}

/* --- Custom per-variant gallery.
       Slide order is [variant's default product image, ...per-color VIEWS urls].
       The default image is ALWAYS slide 0 and is deliberately absent from the
       VIEWS strip, so switching color always lands back on the product shot
       while the thumbnails stay strictly the merchant-supplied URLs. --- */
// 1x1 transparent GIF: a parked <img> that costs no request.
var ROSE_BLANK_PX = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
if (!customElements.get('rose-gallery')) {
  customElements.define(
    'rose-gallery',
    class RoseGallery extends HTMLElement {
      connectedCallback() {
        this.views = this.readJson('script[data-rose-gallery-data]');
        this.defaults = this.readJson('script[data-rose-gallery-defaults]');
        var self = this;
        Object.keys(this.views).forEach(function (k) {
          self.views[k] = (self.views[k] || [])
            .map(function (s) { return String(s).trim(); })
            .filter(Boolean);
        });
        this.mainImg = this.querySelector('[data-rose-gallery-main]');
        this.thumbsEl = this.querySelector('[data-rose-gallery-thumbs]');
        var prev = this.querySelector('[data-rose-gallery-prev]');
        var next = this.querySelector('[data-rose-gallery-next]');
        if (prev) prev.addEventListener('click', () => this.step(-1));
        if (next) next.addEventListener('click', () => this.step(1));
        if (this.thumbsEl) {
          this.thumbsEl.addEventListener('click', (e) => {
            var b = e.target.closest('[data-rose-gallery-index]');
            if (!b) return;
            // Thumb 0 is VIEWS #1, which is slide 1 (slide 0 is the default shot).
            this.index = parseInt(b.dataset.roseGalleryIndex, 10) + this.thumbOffset;
            this.render();
          });
        }
        this.setVariant(
          this.dataset.initial || document.documentElement.getAttribute('data-rose-theme')
        );
        // Swipe the main image, in addition to the arrows. Gated to the Galaxy
        // and Glitter pages for now - the Lamp's Multi-Color layered stage is
        // left on arrows only, since slide 0 there is covered by the animated
        // color layers rather than the plain <img> this drags.
        var swipeHost = this.querySelector('.rose-custom-gallery__main');
        var swipeOk = document.documentElement.hasAttribute('data-rose-stars') ||
          document.documentElement.hasAttribute('data-rose-glitter');
        if (swipeHost && swipeOk) this.bindSwipe(swipeHost);
        // Warm every other variant's default image while the page is idle, so
        // switching colors swaps instantly instead of holding the old shot
        // for the network+decode beat the shopper can see.
        var idle = window.requestIdleCallback || function (fn) { setTimeout(fn, 1200); };
        idle(function () { self.preloadDefaults(); });
      }
      // Pointer Events so one path covers touch, pen and mouse click-drag. The
      // gesture is abandoned the moment it turns mostly vertical, otherwise the
      // swipe would swallow the page scroll.
      bindSwipe(host) {
        var self = this;
        var startX = 0;
        var startY = 0;
        var tracking = false;
        // Let the browser keep vertical scrolling; we only claim horizontal.
        host.style.touchAction = 'pan-y';
        host.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          startX = e.clientX;
          startY = e.clientY;
          tracking = true;
        });
        host.addEventListener('pointermove', function (e) {
          if (!tracking) return;
          if (Math.abs(e.clientY - startY) > Math.abs(e.clientX - startX)) {
            tracking = false; // vertical: this is a page scroll, not a swipe
          }
        });
        function end(e) {
          if (!tracking) return;
          tracking = false;
          var dx = e.clientX - startX;
          if (Math.abs(dx) < 40) return; // too small to be intentional
          self.step(dx < 0 ? 1 : -1);
          // A drag on an <img> would otherwise still fire a click (and could
          // open the lightbox); swallow the one click that follows this swipe.
          host.addEventListener('click', function stop(ev) {
            ev.stopPropagation();
            ev.preventDefault();
            host.removeEventListener('click', stop, true);
          }, true);
        }
        host.addEventListener('pointerup', end);
        host.addEventListener('pointercancel', function () { tracking = false; });
      }
      preloadDefaults() {
        var sized = window.RosePDP.cdnResize;
        var defaults = this.defaults || {};
        Object.keys(defaults).forEach(function (slug) {
          var url = defaults[slug];
          // Multi-Color's stage is built from the three layered webps; its
          // default (the heavy animated GIF) is deliberately never fetched
          // until that color is actually selected.
          if (!url || slug === 'multi-color') return;
          var pre = new Image();
          // Mirror render()'s srcset/sizes exactly, so the SAME candidate the
          // gallery will pick later is the one already sitting in the cache.
          pre.sizes = '(min-width: 750px) 640px, 100vw';
          pre.srcset =
            sized(url, 600) + ' 600w, ' +
            sized(url, 800) + ' 800w, ' +
            sized(url, 1200) + ' 1200w';
          pre.src = sized(url, 800);
          if (pre.decode) pre.decode().catch(function () {});
        });
      }
      readJson(sel) {
        var el = this.querySelector(sel);
        if (!el) return {};
        try { return JSON.parse(el.textContent) || {}; } catch (e) { return {}; }
      }
      // The VIEW url boxes are keyed by plain color names (red, pink, ...) but a
      // product's option values may be wordier - the Glitter Rose ships "Red
      // Rose", which handleizes to "red-rose" and matched nothing. Fall back to
      // the color word so a merchant filling in "Red image URLs" gets what they
      // expect on every product.
      pick(map, slug) {
        if (!map) return undefined;
        if (map[slug] !== undefined) return map[slug];
        var alt = slug.replace(/-(rose|lamp|display)$/, '');
        if (map[alt] !== undefined) return map[alt];
        return undefined;
      }
      setVariant(slug) {
        var views = this.pick(this.views, slug) || [];
        var def = this.pick(this.defaults, slug) || '';
        if (!def && !views.length) return; // nothing configured for this color
        this.thumbOffset = def ? 1 : 0;
        this.list = def ? [def].concat(views) : views;
        this.index = 0; // always land on the default product image
        this.renderThumbs(views);
        this.render();
      }
      renderThumbs(views) {
        if (!this.thumbsEl) return;
        var sized = window.RosePDP.cdnResize;
        this.thumbsEl.innerHTML = views
          .map(function (url, i) {
            return (
              '<button type="button" class="rose-gallery-thumb" data-rose-gallery-index="' + i + '">' +
              '<img src="' + sized(url, 200).replace(/"/g, '&quot;') +
              '" srcset="' + sized(url, 200).replace(/"/g, '&quot;') + ' 1x, ' +
              sized(url, 400).replace(/"/g, '&quot;') + ' 2x" alt="View ' + (i + 1) +
              '" width="200" height="200" loading="lazy" decoding="async"></button>'
            );
          })
          .join('');
      }
      render() {
        if (!this.list || !this.list.length) return;
        if (this.mainImg) {
          var url = this.list[this.index];
          var sized = window.RosePDP.cdnResize;
          // On slide 0 under Multi-Color the layered lamp covers this <img>
          // completely. `visibility: hidden` does NOT stop a download, so
          // pointing it at the variant image here would still pull the 1.1 MB
          // color-cycling GIF and undo everything the three 168 KB layers buy.
          // Park it on a 1x1 instead; other slides load normally.
          var coveredByLayers =
            this.index === 0 &&
            this.thumbOffset === 1 &&
            document.documentElement.getAttribute('data-rose-theme') === 'multi-color' &&
            !!this.querySelector('.rose-multi');
          if (!this.glowImg) this.glowImg = this.querySelector('[data-rose-gallery-glow]');
          if (!this.glintEl) this.glintEl = this.querySelector('.rose-custom-gallery__glint');
          if (coveredByLayers) {
            this.mainImg.src = ROSE_BLANK_PX;
            this.mainImg.removeAttribute('srcset');
            if (this.glowImg) {
              this.glowImg.src = ROSE_BLANK_PX;
              this.glowImg.removeAttribute('srcset');
            }
          } else {
            if (this.glowImg) this.glowImg.src = sized(url, 800);
            if (this.glintEl) {
              // Clip the glint band to the product's own alpha silhouette.
              var glintMask = 'url("' + sized(url, 800) + '")';
              this.glintEl.style.webkitMaskImage = glintMask;
              this.glintEl.style.maskImage = glintMask;
            }
            this.mainImg.src = sized(url, 800);
            this.mainImg.srcset =
              sized(url, 600) + ' 600w, ' +
              sized(url, 800) + ' 800w, ' +
              sized(url, 1200) + ' 1200w';
            this.mainImg.sizes = '(min-width: 750px) 640px, 100vw';
          }
        }
        // Slide 0 is the cut-out product shot; the rest are lifestyle photos.
        // Only the product shot gets the glow treatment.
        this.classList.toggle('is-product-shot', this.index === 0 && this.thumbOffset === 1);
        if (this.thumbsEl) {
          var active = this.index - this.thumbOffset;
          this.thumbsEl.querySelectorAll('.rose-gallery-thumb').forEach((t, i) => {
            t.classList.toggle('is-active', i === active);
          });
        }
      }
      step(dir) {
        if (!this.list || !this.list.length) return;
        this.index = (this.index + dir + this.list.length) % this.list.length;
        this.render();
      }
    }
  );
}

/* --- Customer photo strip: auto-advances one photo at a time and wraps.
       Pauses while the shopper is hovering or actively scrolling it. --- */
if (!customElements.get('rose-photo-strip')) {
  customElements.define(
    'rose-photo-strip',
    class RosePhotoStrip extends HTMLElement {
      connectedCallback() {
        this.strip = this.querySelector('.rose-photo-strip') || this;
        if (this.strip.children.length < 2) return;
        this.paused = false;
        var pause = () => { this.paused = true; };
        var resume = () => { this.paused = false; };
        this.addEventListener('mouseenter', pause);
        this.addEventListener('mouseleave', resume);
        this.addEventListener('touchstart', pause, { passive: true });
        this.addEventListener('touchend', () => setTimeout(resume, 4000), { passive: true });
        // Only run while on screen - no point scrolling a carousel nobody sees,
        // and it keeps the main thread free during page load.
        this.onScreen = false;
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver((e) => {
            this.onScreen = e[0].isIntersecting;
          }, { rootMargin: '120px' });
          this.io.observe(this);
        } else {
          this.onScreen = true;
        }
        this.timer = setInterval(() => {
          if (this.onScreen) this.next();
        }, 3000);
      }
      next() {
        if (this.paused || this.animating) return;
        var s = this.strip;
        var first = s.children[0];
        if (!first) return;
        var step = first.getBoundingClientRect().width + 6; // photo + gap
        var atEnd = s.scrollLeft + s.clientWidth >= s.scrollWidth - 4;
        this.glideTo(atEnd ? 0 : s.scrollLeft + step);
      }
      // Stepped with a timer rather than `behavior:'smooth'` or
      // requestAnimationFrame: smooth scrolling is a no-op on scroll containers
      // in some engines, and rAF is throttled when the strip is off screen -
      // which is exactly when this carousel is usually rotating.
      glideTo(target) {
        var s = this.strip;
        var start = s.scrollLeft;
        var delta = target - start;
        if (!delta) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          s.scrollLeft = target;
          return;
        }
        var steps = 24;
        var i = 0;
        this.animating = true;
        clearInterval(this.anim);
        this.anim = setInterval(() => {
          i += 1;
          var p = i / steps;
          var eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
          s.scrollLeft = start + delta * eased;
          if (i >= steps) {
            clearInterval(this.anim);
            this.animating = false;
          }
        }, 20);
      }
      disconnectedCallback() {
        clearInterval(this.timer);
        clearInterval(this.anim);
      }
    }
  );
}

/* --- Facebook-comment slider: auto-rotates its slides --- */
if (!customElements.get('rose-fb-slider')) {
  customElements.define(
    'rose-fb-slider',
    class RoseFbSlider extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('.rose-fb-slide'));
        this.dots = Array.from(this.querySelectorAll('.rose-fb-dot'));
        if (this.slides.length < 2) return;
        this.index = 0;
        this.paused = false;
        this.bindDots();
        this.bindSwipe();
        // Idle while off screen, so it costs nothing during page load.
        this.onScreen = false;
        if ('IntersectionObserver' in window) {
          this.io = new IntersectionObserver((e) => {
            this.onScreen = e[0].isIntersecting;
          }, { rootMargin: '120px' });
          this.io.observe(this);
        } else {
          this.onScreen = true;
        }
        this.addEventListener('mouseenter', () => { this.paused = true; });
        this.addEventListener('mouseleave', () => { this.paused = false; });
        this.timer = setInterval(() => {
          if (this.onScreen && !this.paused) this.next();
        }, 4500);
      }
      bindDots() {
        var self = this;
        this.dots.forEach(function (dot) {
          dot.addEventListener('click', function () {
            self.go(parseInt(dot.dataset.roseFbDot, 10));
            // The shopper is driving now; stop yanking the slide out from
            // under them a couple of seconds later.
            self.stopAuto();
          });
        });
      }
      // Swipe, using Pointer Events so one code path covers touch, pen and a
      // click-drag with a mouse. The gesture is abandoned if the drag turns out
      // to be mostly vertical, otherwise the slider would eat page scrolling.
      bindSwipe() {
        var self = this;
        var startX = 0;
        var startY = 0;
        var tracking = false;
        this.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          if (e.target.closest('.rose-fb-dot')) return; // let the dot handle it
          startX = e.clientX;
          startY = e.clientY;
          tracking = true;
        });
        this.addEventListener('pointermove', function (e) {
          if (!tracking) return;
          if (Math.abs(e.clientY - startY) > Math.abs(e.clientX - startX)) {
            tracking = false; // vertical: this is a page scroll, not a swipe
          }
        });
        function end(e) {
          if (!tracking) return;
          tracking = false;
          var dx = e.clientX - startX;
          if (Math.abs(dx) < 40) return; // too small to be intentional
          self.go(self.index + (dx < 0 ? 1 : -1));
          self.stopAuto();
        }
        this.addEventListener('pointerup', end);
        this.addEventListener('pointercancel', function () { tracking = false; });
      }
      go(i) {
        var n = this.slides.length;
        this.index = ((i % n) + n) % n; // wraps in both directions
        this.slides.forEach((s, j) => s.classList.toggle('is-current', j === this.index));
        this.dots.forEach((d, j) => {
          d.classList.toggle('is-current', j === this.index);
          d.setAttribute('aria-current', j === this.index ? 'true' : 'false');
        });
      }
      next() {
        this.go(this.index + 1);
      }
      stopAuto() {
        clearInterval(this.timer);
        this.timer = null;
      }
      disconnectedCallback() {
        clearInterval(this.timer);
      }
    }
  );
}

/* --- "Turn out the lights": dims the panel around the product and blooms the
       glow, so the page can answer "what does it look like in a dark room?"
       without a second photograph. Pure class toggle - the look lives in CSS. --- */
if (!customElements.get('rose-after-dark')) {
  customElements.define(
    'rose-after-dark',
    class RoseAfterDark extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('[data-rose-dark-toggle]');
        this.label = this.querySelector('[data-rose-dark-label]');
        this.caption = this.querySelector('[data-rose-dark-caption]');
        if (!this.btn) return;
        // Read the labels off the markup once, so the merchant's wording in the
        // theme editor flows straight through without being duplicated here.
        this.labels = {
          off: this.label ? this.label.textContent.trim() : 'Turn out the lights',
          on: this.dataset.labelOn || ''
        };
        this.captions = {
          off: this.caption ? this.caption.textContent.trim() : '',
          on: this.dataset.captionOff || ''
        };
        this.dark = false;
        this.btn.addEventListener('click', () => this.toggle());
      }
      toggle() {
        this.dark = !this.dark;
        this.classList.toggle('is-dark', this.dark);
        this.btn.setAttribute('aria-pressed', this.dark ? 'true' : 'false');
        if (this.label && this.labels.on) {
          this.label.textContent = this.dark ? this.labels.on : this.labels.off;
        }
        if (this.caption && this.captions.on) {
          this.caption.textContent = this.dark ? this.captions.on : this.captions.off;
        }
      }
    }
  );
}

/* --- Gift note: pick an occasion, get the words for the card, copy them. --- */
if (!customElements.get('rose-gift-note')) {
  customElements.define(
    'rose-gift-note',
    class RoseGiftNote extends HTMLElement {
      connectedCallback() {
        var data = this.querySelector('script[data-rose-note-data]');
        try {
          this.messages = data ? JSON.parse(data.textContent) : [];
        } catch (e) {
          this.messages = [];
        }
        this.messageEl = this.querySelector('[data-rose-note-message]');
        this.copyBtn = this.querySelector('[data-rose-note-copy]');
        this.copyLabel = this.querySelector('[data-rose-note-copy-label]');
        this.copyText = this.copyLabel ? this.copyLabel.textContent.trim() : 'Copy';
        this.copiedText = this.dataset.copiedLabel || 'Copied';

        this.addEventListener('change', (e) => {
          var input = e.target.closest('[data-rose-note-input]');
          if (!input) return;
          this.select(parseInt(input.value, 10));
        });
        if (this.copyBtn) this.copyBtn.addEventListener('click', () => this.copy());
      }
      select(i) {
        if (!this.messages[i]) return;
        if (this.messageEl) this.messageEl.textContent = this.messages[i];
        this.querySelectorAll('.rose-gift-chip').forEach(function (chip, j) {
          chip.classList.toggle('is-current', j === i);
        });
        this.resetCopyLabel();
      }
      copy() {
        var text = this.messageEl ? this.messageEl.textContent.trim() : '';
        if (!text) return;
        var self = this;
        var done = function () { self.flashCopied(); };
        // The async clipboard API needs a secure context and can be blocked;
        // the textarea + execCommand path is the fallback that still works.
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(text).then(done, function () { self.legacyCopy(text, done); });
        } else {
          this.legacyCopy(text, done);
        }
      }
      legacyCopy(text, done) {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:absolute;left:-9999px;top:0;';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); done(); } catch (e) { /* nothing we can do */ }
        document.body.removeChild(ta);
      }
      flashCopied() {
        if (!this.copyLabel) return;
        this.copyLabel.textContent = this.copiedText;
        this.copyBtn.classList.add('is-copied');
        clearTimeout(this.copyTimer);
        var self = this;
        this.copyTimer = setTimeout(function () { self.resetCopyLabel(); }, 2200);
      }
      resetCopyLabel() {
        if (!this.copyLabel) return;
        this.copyLabel.textContent = this.copyText;
        this.copyBtn.classList.remove('is-copied');
      }
    }
  );
}

/* --- Final CTA: hide the sticky ATC while this section is on screen --- */
if (!customElements.get('rose-final-cta')) {
  customElements.define(
    'rose-final-cta',
    class RoseFinalCta extends HTMLElement {
      connectedCallback() {
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            function (entries) {
              document.body.classList.toggle(
                'rose-final-cta-visible',
                entries[0].isIntersecting
              );
            },
            { threshold: 0.2 }
          );
          this.observer.observe(this);
        }
      }
      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        document.body.classList.remove('rose-final-cta-visible');
      }
    }
  );
}

/* --- Any rose ATC proxy button: click the real add-to-cart --- */
if (!window.roseAtcProxyBound) {
  window.roseAtcProxyBound = true;
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-rose-atc-proxy]');
    if (btn) {
      event.preventDefault();
      // Add the selected variant and stay put. It used to scroll the buy box
      // into view, which yanked the page away from whatever the shopper was
      // reading - and on the live theme the cart drawer slides in from the
      // right anyway, so the confirmation is already visible.
      var realBtn = document.querySelector(
        '.rose-main-section product-form button[name="add"], .rose-main-section button[name="add"]'
      );
      if (realBtn) realBtn.click();
      return;
    }
    // Back-to-top: for CTAs that should return the shopper to the picker
    // rather than add anything to the cart.
    var top = event.target.closest('[data-rose-scroll-top]');
    if (top) {
      event.preventDefault();
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    }
  });
}

/* ============================ HOMEPAGE ELEMENTS ============================ */

/* --- Hero rotator: cross-fades hero slides and re-tints the whole page to
       each slide's color, so the cosmos shifts with the product on screen. --- */
if (!customElements.get('rose-hero-rotator')) {
  customElements.define(
    'rose-hero-rotator',
    class RoseHeroRotator extends HTMLElement {
      connectedCallback() {
        this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-rose-slide]'));
        if (this.slides.length < 2) return;
        this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-rose-dot]'));
        this.index = 0;
        this.delay = parseInt(this.dataset.interval || '5200', 10);
        var self = this;
        this.dots.forEach(function (dot, i) {
          dot.addEventListener('click', function () {
            self.go(i);
            self.restart();
          });
        });
        // Warm the other slides so a cross-fade never shows a half-loaded image.
        this.slides.forEach(function (slide) {
          var img = slide.querySelector('img');
          if (img && img.dataset.roseSrc && !img.getAttribute('src')) {
            img.setAttribute('src', img.dataset.roseSrc);
          }
        });
        // --- Swipe / drag. Pointer events cover touch, pen and mouse in one
        //     path. The listeners sit on the rotator itself, which spans the
        //     full width of the hero, so the grab area is far wider than the
        //     image and hard to miss. ---
        var startX = 0, startY = 0, tracking = false;
        this.addEventListener('pointerdown', function (event) {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          startX = event.clientX;
          startY = event.clientY;
          tracking = true;
          // Capture so the release still reaches us if the finger drifts off.
          if (self.setPointerCapture) {
            try { self.setPointerCapture(event.pointerId); } catch (e) { /* not capturable */ }
          }
          self.stop();
        });
        this.addEventListener('pointermove', function (event) {
          if (!tracking) return;
          // A mostly-vertical drag is the shopper scrolling the page, not
          // swiping the hero - let go of it so scrolling stays natural.
          var dx = Math.abs(event.clientX - startX);
          var dy = Math.abs(event.clientY - startY);
          if (dy > dx && dy > 12) tracking = false;
        });
        var release = function (event) {
          if (!tracking) { self.restart(); return; }
          tracking = false;
          var dx = event.clientX - startX;
          if (Math.abs(dx) > 40) {
            var step = dx < 0 ? 1 : -1;   // drag left = next
            self.go((self.index + step + self.slides.length) % self.slides.length);
          }
          self.restart();
        };
        this.addEventListener('pointerup', release);
        this.addEventListener('pointercancel', function () { tracking = false; self.restart(); });
        // Images are draggable by default, which hijacks the swipe.
        this.querySelectorAll('img').forEach(function (img) {
          img.addEventListener('dragstart', function (e) { e.preventDefault(); });
        });

        this.reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
        if (this.reduce) return; // swipe still works; nothing moves on its own
        this.start();
        // Stop cycling (and re-tinting) once the hero scrolls out of view.
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) self.start();
              else self.stop();
            });
          });
          this.observer.observe(this);
        }
      }
      go(i) {
        this.index = i;
        this.slides.forEach(function (slide, n) { slide.classList.toggle('is-current', n === i); });
        this.dots.forEach(function (dot, n) { dot.classList.toggle('is-current', n === i); });
        var theme = this.slides[i].dataset.roseSlideTheme;
        if (theme && window.RosePDP) window.RosePDP.setTheme(theme);
      }
      start() {
        // Swiping calls restart(), so the reduced-motion opt-out has to be
        // enforced here rather than only at set-up.
        if (this.timer || this.reduce) return;
        var self = this;
        this.timer = setInterval(function () {
          self.go((self.index + 1) % self.slides.length);
        }, this.delay);
      }
      stop() {
        if (!this.timer) return;
        clearInterval(this.timer);
        this.timer = null;
      }
      restart() { this.stop(); this.start(); }
      disconnectedCallback() {
        this.stop();
        if (this.observer) this.observer.disconnect();
      }
    }
  );
}

/* --- Before/after slider. A real <input type="range"> drives the split, so
       dragging, tapping and keyboard control all work with no pointer code. --- */
if (!customElements.get('rose-toggle')) {
  customElements.define(
    'rose-toggle',
    class RoseToggle extends HTMLElement {
      connectedCallback() {
        this.frame = this.querySelector('.rose-toggle__frame');
        this.range = this.querySelector('.rose-toggle__range');
        if (!this.frame || !this.range) return;
        var self = this;
        var apply = function () {
          self.frame.style.setProperty('--rose-split', self.range.value + '%');
        };
        // Anchors for the attention sweep: it swings a long way left to show
        // the lit room, then returns to wherever the merchant set the handle.
        var rest = parseFloat(this.range.value);
        if (isNaN(rest)) rest = 75;
        this.frame.style.setProperty('--rose-rest', rest + '%');
        this.frame.style.setProperty('--rose-peek', Math.max(6, rest - 34) + '%');
        this.range.addEventListener('input', function () {
          // The slider stops advertising itself once it has been used.
          self.classList.add('is-touched');
          apply();
        });
        apply();
      }
    }
  );
}

/* --- Falling petals.
       Builds the petals in JS so every one gets its own size, speed, sway and
       delay - a tiled background can only ever repeat, which is what made the
       first attempt read as texture rather than motion. Two nested elements:
       the outer falls, the inner sways and turns, so the two motions compose
       without fighting over one transform. --- */
if (!customElements.get('rose-petals')) {
  customElements.define(
    'rose-petals',
    class RosePetals extends HTMLElement {
      connectedCallback() {
        if (this.dataset.built) return;
        this.dataset.built = '1';
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce) return;
        // The layer is fixed, so it must hang off the body - inside a section a
        // transformed or contained ancestor could clip it.
        if (this.parentElement !== document.body) document.body.appendChild(this);

        var count = parseInt(this.dataset.count || '24', 10);
        if (window.innerWidth < 750) count = Math.round(count * 0.6);
        var tints = ['a', 'b', 'c', 'd'];
        var frag = document.createDocumentFragment();

        for (var i = 0; i < count; i++) {
          var petal = document.createElement('span');
          petal.className = 'rose-petal rose-petal--' + tints[i % tints.length];
          var size = 13 + Math.random() * 26;   // 13-39px
          var depth = (size - 13) / 26;         // 0 = far, 1 = near
          petal.style.left = (Math.random() * 100).toFixed(2) + '%';
          // A rose petal is about as wide as it is tall; the slight variance
          // stops every petal from being a clone of the last.
          petal.style.width = size.toFixed(1) + 'px';
          petal.style.height = (size * (0.94 + Math.random() * 0.16)).toFixed(1) + 'px';
          petal.style.opacity = (0.4 + depth * 0.45).toFixed(2);
          // Near petals fall faster; the negative delay seeds mid-flight so the
          // sky is already full on load instead of starting empty.
          petal.style.animationDuration = (26 - depth * 11 + Math.random() * 5).toFixed(1) + 's';
          petal.style.animationDelay = (-Math.random() * 30).toFixed(1) + 's';
          if (depth < 0.45) petal.style.filter = 'blur(' + (1.5 - depth * 2.2).toFixed(2) + 'px)';

          var inner = document.createElement('i');
          inner.style.animationDuration = (3.2 + Math.random() * 4).toFixed(1) + 's';
          inner.style.animationDelay = (-Math.random() * 7).toFixed(1) + 's';
          petal.appendChild(inner);
          frag.appendChild(petal);
        }
        this.appendChild(frag);
      }
    }
  );
}

/* --- Glitter: four-point sparkles that wink in and out.
       Built in JS for the same reason as the petals - every glint needs its own
       position, size, rhythm and delay, which a repeating background cannot do.
       Twinkle only, no travel: the sparkle should read as light catching the
       glass, not as something falling. --- */
if (!customElements.get('rose-glitter')) {
  customElements.define(
    'rose-glitter',
    class RoseGlitter extends HTMLElement {
      connectedCallback() {
        if (this.dataset.built) return;
        this.dataset.built = '1';
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        // Fixed layer, so it has to hang off the body to escape any section
        // that establishes a containing block.
        if (this.parentElement !== document.body) document.body.appendChild(this);

        var count = parseInt(this.dataset.count || '34', 10);
        if (window.innerWidth < 750) count = Math.round(count * 0.6);
        var tints = ['a', 'b', 'c'];
        var frag = document.createDocumentFragment();

        for (var i = 0; i < count; i++) {
          var spark = document.createElement('span');
          spark.className = 'rose-sparkle rose-sparkle--' + tints[i % tints.length];
          var size = 7 + Math.random() * 15;   // 7-22px
          spark.style.left = (Math.random() * 100).toFixed(2) + '%';
          spark.style.top = (Math.random() * 100).toFixed(2) + '%';
          spark.style.width = size.toFixed(1) + 'px';
          spark.style.height = size.toFixed(1) + 'px';
          // Bigger glints burn slower; the negative delay seeds them mid-twinkle
          // so the field is already alive on load rather than blinking on at once.
          spark.style.animationDuration = (2.6 + Math.random() * 3.6).toFixed(1) + 's';
          spark.style.animationDelay = (-Math.random() * 6).toFixed(1) + 's';
          if (size < 12) spark.style.filter = 'blur(0.4px)';
          spark.appendChild(document.createElement('i'));
          frag.appendChild(spark);
        }
        this.appendChild(frag);
      }
    }
  );
}

/* --- Homepage product spotlight.
       Self-contained on purpose: its color swatches swap THIS card's image and
       point its button at that exact variant (?variant=<id>), so the product
       page opens already on the color the shopper picked. It deliberately does
       NOT re-theme the page - the hero rotator owns the page's mood. --- */
if (!customElements.get('rose-spotlight')) {
  customElements.define(
    'rose-spotlight',
    class RoseSpotlight extends HTMLElement {
      connectedCallback() {
        this.img = this.querySelector('[data-rose-spot-image]');
        this.cta = this.querySelector('[data-rose-spot-cta]');
        this.swatches = Array.prototype.slice.call(this.querySelectorAll('[data-rose-spot-swatch]'));
        if (!this.swatches.length) return;
        this.baseHref = this.cta ? (this.cta.getAttribute('href') || '').split('?')[0] : '';
        var self = this;
        this.addEventListener('click', function (event) {
          var sw = event.target.closest('[data-rose-spot-swatch]');
          if (sw && self.contains(sw)) self.select(sw);
        });
        // Start on whichever swatch is marked active, else the first one, so the
        // dot, the photo and the button always agree.
        var initial = this.swatches.filter(function (s) { return s.classList.contains('is-active'); })[0] || this.swatches[0];
        this.select(initial);
        // Warm the other colors so switching is instant rather than a fetch.
        var idle = window.requestIdleCallback || function (fn) { setTimeout(fn, 1200); };
        idle(function () {
          self.swatches.forEach(function (s) {
            if (!s.dataset.image) return;
            var pre = new Image();
            pre.src = s.dataset.image;
            if (pre.decode) pre.decode().catch(function () {});
          });
        });
      }
      select(sw) {
        this.swatches.forEach(function (s) { s.classList.toggle('is-active', s === sw); });
        if (this.img && sw.dataset.image && this.img.getAttribute('src') !== sw.dataset.image) {
          this.img.setAttribute('src', sw.dataset.image);
          this.img.removeAttribute('srcset');
        }
        if (this.cta && this.baseHref && sw.dataset.variant) {
          this.cta.setAttribute('href', this.baseHref + '?variant=' + sw.dataset.variant);
        }
      }
    }
  );
}

/* --- "Find her Rose" finder: each answer votes for a product handle; the
       most-voted product is revealed at the end. --- */
if (!customElements.get('rose-finder')) {
  customElements.define(
    'rose-finder',
    class RoseFinder extends HTMLElement {
      connectedCallback() {
        this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-rose-step]'));
        this.result = this.querySelector('[data-rose-result]');
        this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-rose-result-for]'));
        if (!this.steps.length) return;
        var self = this;
        this.addEventListener('click', function (event) {
          var opt = event.target.closest('[data-rose-vote]');
          if (opt && self.contains(opt)) {
            var step = opt.closest('[data-rose-step]');
            if (step) {
              step.querySelectorAll('[data-rose-vote]').forEach(function (o) {
                o.classList.toggle('is-chosen', o === opt);
              });
            }
            self.votes[opt.dataset.roseVote] = (self.votes[opt.dataset.roseVote] || 0) + 1;
            self.advance();
            return;
          }
          if (event.target.closest('[data-rose-restart]')) self.reset();
        });
        this.reset();
      }
      reset() {
        this.votes = {};
        this.step = 0;
        this.querySelectorAll('.is-chosen').forEach(function (o) { o.classList.remove('is-chosen'); });
        this.show(0);
      }
      show(n) {
        this.steps.forEach(function (step, i) { step.hidden = i !== n; });
        if (this.result) this.result.hidden = true;
      }
      advance() {
        this.step += 1;
        if (this.step < this.steps.length) {
          this.show(this.step);
          return;
        }
        this.steps.forEach(function (step) { step.hidden = true; });
        // Highest vote wins; ties fall to whichever was answered first.
        var votes = this.votes;
        var winner = null;
        var best = -1;
        Object.keys(votes).forEach(function (key) {
          if (votes[key] > best) { best = votes[key]; winner = key; }
        });
        this.cards.forEach(function (card) {
          card.hidden = card.dataset.roseResultFor !== winner;
        });
        // Nothing matched a configured card - show the first as a safe default.
        if (winner && !this.cards.some(function (c) { return !c.hidden; }) && this.cards[0]) {
          this.cards[0].hidden = false;
        }
        if (this.result) this.result.hidden = false;
      }
    }
  );
}
